import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { api } from './api/client';
import { useAppDispatch, useAppSelector } from './app/store';
import { setAccessToken, setCredentials, setBootstrapped } from './features/auth/authSlice';
import { SplashScreen } from './components/SplashScreen';
import { ProtectedRoute } from './routes/ProtectedRoute';
import { AdminLayout } from './layouts/AdminLayout';
import { ClientLayout } from './layouts/ClientLayout';
import LoginPage from './pages/LoginPage';
import TradeActionPage from './pages/TradeActionPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import ApprovalsPage from './pages/admin/ApprovalsPage';
import ClientsPage from './pages/admin/ClientsPage';
import ClientDetailPage from './pages/admin/ClientDetailPage';
import ClientDashboard from './pages/client/ClientDashboard';
import TradePage from './pages/client/TradePage';
import DeribitTradePage from './pages/client/DeribitTradePage';
import OrdersPage from './pages/client/OrdersPage';
import PnlPage from './pages/client/PnlPage';

export default function App() {
  const { isAuthenticated, isAdmin, isViewOnly } = useAuth();
  const dispatch = useAppDispatch();
  const bootstrapped = useAppSelector((s) => s.auth.bootstrapped);

  // On first load (incl. hard refresh) try to restore the session from the
  // httpOnly refresh cookie before deciding whether to show the login page.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api.post('/auth/refresh', {});
        const token = res.data.data.accessToken as string;
        dispatch(setAccessToken(token));
        const me = await api.get('/auth/me');
        if (active) dispatch(setCredentials({ accessToken: token, user: me.data.data }));
      } catch {
        /* No valid session — remain logged out. */
      } finally {
        if (active) dispatch(setBootstrapped());
      }
    })();
    return () => {
      active = false;
    };
  }, [dispatch]);

  if (!bootstrapped) return <SplashScreen />;

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" /> : <LoginPage />} />

      {/* Public one-click email approve/reject — token-authed, no login required. */}
      <Route path="/trade-action/:token" element={<TradeActionPage />} />

      {/* Admin area */}
      <Route element={<ProtectedRoute allow="ADMIN" />}>
        <Route element={<AdminLayout />}>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/approvals" element={<ApprovalsPage />} />
          <Route path="/admin/clients" element={<ClientsPage />} />
          <Route path="/admin/clients/:id" element={<ClientDetailPage />} />
        </Route>
      </Route>

      {/* Client area */}
      <Route element={<ProtectedRoute allow="CLIENT" />}>
        <Route element={<ClientLayout />}>
          <Route path="/dashboard" element={<ClientDashboard />} />
          {/* View-only clients have no trading ticket — send them back to the dashboard. */}
          <Route path="/trade" element={isViewOnly ? <Navigate to="/dashboard" replace /> : <TradePage />} />
          <Route path="/trade/deribit" element={isViewOnly ? <Navigate to="/dashboard" replace /> : <DeribitTradePage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/pnl" element={<PnlPage />} />
        </Route>
      </Route>

      <Route path="/" element={<Navigate to={isAdmin ? '/admin' : '/dashboard'} />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}
